import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import type { DealDeadline } from '../../types';
import DeadlineCountdown from './DeadlineCountdown';
import ContractTimelineReview from './ContractTimelineReview';
import { deadlineDate, byDueDate } from '../../utils/deadlineDates';

interface Props {
  dealId: string;
  escrowOpenedDate?: string | null;
  onAnchorChanged?: () => void;
}

function EditableDate({ value, onSave }: { value: string | null; onSave: (d: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value?.slice(0, 10) || '');
  if (!editing) {
    return (
      <button onClick={() => { setDraft(value?.slice(0, 10) || ''); setEditing(true); }}
        className="text-xs text-gray-600 hover:text-primary-600" title="Edit date">
        {deadlineDate(value) ?? 'Set date'}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="border rounded px-1 py-0.5 text-xs" />
      <button onClick={() => { if (draft) onSave(draft); setEditing(false); }} className="text-xs text-primary-500">Save</button>
      <button onClick={() => setEditing(false)} className="text-xs text-gray-400">Cancel</button>
    </span>
  );
}

export default function DeadlinesPanel({ dealId, escrowOpenedDate, onAnchorChanged }: Props) {
  const [rows, setRows] = useState<DealDeadline[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState({ label: '', due_date: '' });
  const [anchor, setAnchor] = useState(escrowOpenedDate?.slice(0, 10) || '');
  const [showContract, setShowContract] = useState(false);

  useEffect(() => { setAnchor(escrowOpenedDate?.slice(0, 10) || ''); }, [escrowOpenedDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows([...((await api.listDealDeadlines(dealId)).deadlines || [])].sort(byDueDate)); }
    finally { setLoading(false); }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (d: DealDeadline) => { await api.updateDealDeadline(d.id, { is_completed: !d.is_completed }); load(); };
  const setDate = async (d: DealDeadline, due_date: string) => { await api.updateDealDeadline(d.id, { due_date }); load(); };
  const remove = async (d: DealDeadline) => { await api.deleteDealDeadline(d.id); load(); };
  const add = async () => {
    if (!adding.label) return;
    await api.addDealDeadline(dealId, { label: adding.label, due_date: adding.due_date });
    setAdding({ label: '', due_date: '' });
    load();
  };
  const saveAnchor = async () => {
    if (!anchor) return;
    const anchoredCount = rows.filter((r) => r.offset_days != null).length;
    if (!window.confirm(`Set escrow-open date to ${anchor}? This recomputes ${anchoredCount} anchored deadline(s).`)) return;
    await api.setEscrowOpenDate(dealId, anchor);
    await load();
    onAnchorChanged?.();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
        <span className="text-sm text-gray-600 flex-1">Escrow open date (Day 1)</span>
        <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        <button onClick={saveAnchor} disabled={!anchor} className="px-3 py-1 bg-primary-500 text-white rounded text-xs disabled:opacity-50">Set + recompute</button>
        <button onClick={() => setShowContract(true)} className="px-3 py-1 border rounded text-xs">Set up from contract</button>
      </div>

      {loading && rows.length === 0 && <div className="text-sm text-gray-400">Loading deadlines...</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-gray-400">No deadlines yet.</div>}
      {rows.map((d) => (
        <div key={d.id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
          <input type="checkbox" checked={d.is_completed} onChange={() => toggle(d)} className="rounded" />
          <span className={`flex-1 text-sm ${d.is_completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{d.label}</span>
          <EditableDate value={d.due_date} onSave={(date) => setDate(d, date)} />
          <DeadlineCountdown dueDate={d.due_date} completed={d.is_completed} />
          <button onClick={() => remove(d)} className="text-gray-300 hover:text-red-500 text-xs">x</button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-2 border-t">
        <input placeholder="Add deadline..." value={adding.label} onChange={(e) => setAdding((a) => ({ ...a, label: e.target.value }))} className="flex-1 border rounded px-2 py-1 text-sm" />
        <input type="date" value={adding.due_date} onChange={(e) => setAdding((a) => ({ ...a, due_date: e.target.value }))} className="border rounded px-2 py-1 text-xs" />
        <button onClick={add} disabled={!adding.label} className="px-3 py-1 bg-primary-500 text-white rounded text-sm disabled:opacity-50">Add</button>
      </div>
      {showContract && (
        <ContractTimelineReview dealId={dealId} onClose={() => setShowContract(false)} onApplied={() => { load(); onAnchorChanged?.(); }} />
      )}
    </div>
  );
}
